import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { usePromotionFlow } from '../../hooks/usePromotionFlow';
import { useDispatch } from 'react-redux';
import { setErrCode, setNumberOfTriesLeft, setCodeSentAt, setIsFail, setSelectedContactToken, setPhone, setFlowToken } from '../../store/mainSlice';
import { PromotionPackageResponseCodes } from '../../types/promotion';
import store from '../../store';
import FlowLayout from '../FlowLayout';
import { useA11yTokens } from '../../hooks/useA11yTokens';
import { useIsDesktopDevice } from '../../hooks/useIsDesktopDevice';

const CELL_COUNT = 6;
const RESEND_SECONDS = 10;
const MAX_ATTEMPTS = 5;

const maskPhone = (phone: string) => '*'.repeat(Math.max(0, phone.length - 3)) + phone.slice(-3);

const OtpCheck: React.FC = () => {
    const { t } = useTranslation();
    const theme4dTokens = useA11yTokens();
    const isDesktop = useIsDesktopDevice();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { verifyCode, sendCode } = usePromotionFlow();
    const { flowToken, phone, isLoading, codeSentAt, codeValidMinutes, numberOfTriesLeft, packageToken, env } = useAppSelector((state) => state.main);
    const [otp, setOtp] = React.useState<string[]>(Array(CELL_COUNT).fill(''));
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [resend, setResend] = React.useState(RESEND_SECONDS);
    const [showInvalidCode, setShowInvalidCode] = React.useState(false);
    const inputsRef = React.useRef<Array<HTMLInputElement | null>>([]);

    React.useEffect(() => {
        if (!flowToken) {
            dispatch(setPhone(''));
            dispatch(setFlowToken(''));
            navigate(packageToken ? `/?token=${encodeURIComponent(packageToken)}&ENV=${env}` : '/');
        }
    }, []);

    React.useEffect(() => {
        inputsRef.current[0]?.focus();
    }, []);

    React.useEffect(() => {
        if (!codeSentAt) return;
        const tick = () => {
            const elapsed = Math.floor((Date.now() - codeSentAt) / 1000);
            setResend(Math.max(0, RESEND_SECONDS - elapsed));
        };
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [codeSentAt]);

    const handleVerified = async (code?: string) => {
        const result = await verifyCode(flowToken, code ?? otp.join(''));
        if (!result) {
            if (store.getState().main.errCode === PromotionPackageResponseCodes.TwoFaCodeWasInvalid) {
                if (store.getState().main.numberOfTriesLeft === 0) {                    
                    dispatch(setErrCode(PromotionPackageResponseCodes.MaxSmsReached));
                    navigate('/error');
                    return;
                }
                dispatch(setIsFail(false));
                setShowInvalidCode(true);
                setOtp(Array(CELL_COUNT).fill(''));
                setActiveIndex(0);
                inputsRef.current[0]?.focus();
                return;
            }
            navigate('/error');
            return;
        }
        dispatch(setNumberOfTriesLeft(MAX_ATTEMPTS));
        if (result.customersContacts.length === 1) {
            dispatch(setSelectedContactToken(result.customersContacts[0].contactToken));
            navigate('/offer');
        } else {
            navigate('/companies');
        }
    };

    const onCellChange = (index: number, value: string) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const next = [...otp];
        next[index] = digit;
        setOtp(next);
        if (digit && index < CELL_COUNT - 1) {
            inputsRef.current[index + 1]?.focus();
        }
        if (digit && next.every((d) => d)) {
            handleVerified(next.join(''));
        }
    };

    const onCellKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const onResend = async () => {
        setOtp(Array(CELL_COUNT).fill(''));
        setShowInvalidCode(false);
        inputsRef.current[0]?.focus();
        const result = await sendCode(flowToken, phone);
        if (result) {
            dispatch(setCodeSentAt(Date.now()));
        } else {
            navigate('/error');
        }
    };

    return (
        <FlowLayout>
            <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 27 : 25, fontWeight: 700, lineHeight: 1.25, color: theme4dTokens.text, m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '50px' }) }}>
                {t('otp.title')}
            </Typography>
            <Typography sx={{ fontSize: isDesktop ? 17 : 15, lineHeight: 1.65, color: theme4dTokens.muted, m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '16px' }) }}>
                {t('otp.leadPrefix')}
                <br />
                <b style={{ color: theme4dTokens.text, direction: 'ltr', unicodeBidi: 'embed' }}>{maskPhone(phone)}</b>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row-reverse', gap: '8px', justifyContent: 'center', my: '4px', ...(isDesktop && { mt: '20px' }) }}>
                {otp.map((value, index) => (
                    <Box
                        key={index}
                        component="input"
                        inputMode="numeric"
                        maxLength={1}
                        ref={(el: HTMLInputElement | null) => (inputsRef.current[index] = el)}
                        value={value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCellChange(index, e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent) => onCellKeyDown(index, e)}
                        onFocus={() => setActiveIndex(index)}
                        sx={{
                            width: '46px',
                            height: '58px',
                            textAlign: 'center',
                            fontSize: isDesktop ? 26 : 24,
                            fontWeight: 700,
                            fontFamily: theme4dTokens.headFont,
                            border: `1.5px solid ${showInvalidCode ? '#dc2626' : index === activeIndex ? theme4dTokens.accent : theme4dTokens.border}`,
                            borderRadius: theme4dTokens.radius,
                            bgcolor: theme4dTokens.surface,
                            color: theme4dTokens.text,
                            outline: 'none',
                            p: 0,
                            boxShadow: index === activeIndex ? `0 0 0 3px ${theme4dTokens.accentSoft}` : 'none',
                        }}
                    />
                ))}
            </Box>
            {showInvalidCode && (
                <Typography sx={{ fontSize: isDesktop ? 15.5 : 13.5, fontWeight: 700, color: '#dc2626', textAlign: 'center', m: 0, wordBreak: 'break-word' }}>
                    {t('otp.invalidCode', { count: numberOfTriesLeft, total: MAX_ATTEMPTS })}
                </Typography>
            )}
            <Typography sx={{ fontSize: isDesktop ? 14.5 : 12.5, color: theme4dTokens.muted, textAlign: 'center', m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '16px' }) }}>{t('otp.validity', { count: codeValidMinutes })}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '20px' }}>
                {resend > 0 ? (
                    <Typography sx={{ fontSize: isDesktop ? 14.5 : 12.5, color: theme4dTokens.muted, m: 0, wordBreak: 'break-word' }}>
                        {t('otp.resendWait', { seconds: resend })}
                    </Typography>
                ) : (
                    <Typography
                        component="button"
                        onClick={onResend}
                        sx={{
                            color: theme4dTokens.accent,
                            fontWeight: 700,
                            fontSize: isDesktop ? 16 : 14,
                            cursor: 'pointer',
                            bgcolor: 'transparent',
                            border: 'none',
                            fontFamily: theme4dTokens.font,
                            p: 0,
                            wordBreak: 'break-word',
                        }}
                    >
                        {t('otp.resend')}
                    </Typography>
                )}
            </Box>
            <Button
                variant="contained"
                fullWidth
                disableElevation
                disabled={isLoading || otp.some((d) => !d)}
                onClick={() => handleVerified()}
                sx={{
                    fontFamily: theme4dTokens.headFont,
                    bgcolor: theme4dTokens.accent,
                    color: theme4dTokens.accentText,
                    borderRadius: theme4dTokens.radius,
                    py: '15px',
                    fontSize: isDesktop ? 18 : 16,
                    fontWeight: 700,
                    mt: 'auto',
                    wordBreak: 'break-word',
                    '&:hover': { bgcolor: theme4dTokens.accent, filter: 'brightness(1.07)' },
                }}
            >
                {t('otp.submit')}
            </Button>
        </FlowLayout>
    );
};

export default OtpCheck;
